import React, { useState, ReactNode, useEffect } from 'react';
import { cn } from '@/shared/utils/utils';
import {
  Tabs as TabsRoot,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/shared/ui/custom/tabs';

interface TabLayoutProps {
  children: ReactNode;
}

interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
}

interface TabsProps {
  tabs: TabItem[];
  defaultTab?: string;
  className?: string;
}

/**
 * Full-height tabbed layout for the interactive viewer.
 *
 * Keeps its convenient array-based API (`tabs`, `defaultTab`) and URL-hash
 * sync, but renders on top of the shared `@/shared/ui/custom/tabs` pill
 * component so there's a single source of truth for tab styling.
 */
export const Tabs: React.FC<TabsProps> = ({ tabs, defaultTab, className }) => {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id);

  // Handle URL hash navigation for tabs
  useEffect(() => {
    const getTabFromHash = () => {
      const hash = window.location.hash.replace('#', '');
      return hash && tabs.some((tab) => tab.id === hash) ? hash : null;
    };

    // Set initial tab from hash if available
    const hashTab = getTabFromHash();
    if (hashTab) {
      setActiveTab(hashTab);
    }

    // Listen for hash changes
    const handleHashChange = () => {
      const hashTab = getTabFromHash();
      if (hashTab) {
        setActiveTab(hashTab);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []); // Removed tabs dependency to prevent infinite loop

  const handleValueChange = (tabId: string) => {
    setActiveTab(tabId);
    // Update URL hash when tab is changed
    window.history.replaceState(null, '', `#${tabId}`);
  };

  return (
    <TabsRoot
      value={activeTab}
      onValueChange={handleValueChange}
      className={cn('w-full h-full flex flex-col overflow-hidden', className)}
    >
      <div className="flex-shrink-0 bg-background/80 backdrop-blur-xl border-b border-border w-full overflow-x-auto">
        <div className="px-6 pt-6 pb-4 w-full max-w-full">
          <TabsList aria-label="Tabs">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                // Stable hook for the guided-tour system: any tab in the
                // viewer can be spotlit via [data-tour-id="iv-tab-<id>"].
                data-tour-id={`iv-tab-${tab.id}`}
              >
                <span className="relative z-10 truncate">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
      </div>

      {tabs.map((tab) => (
        <TabsContent
          key={tab.id}
          value={tab.id}
          className="mt-0 flex-1 min-h-0 overflow-hidden bg-background/30 data-[state=inactive]:hidden"
        >
          {tab.content}
        </TabsContent>
      ))}
    </TabsRoot>
  );
};

const TabLayout: React.FC<TabLayoutProps> = ({ children }) => {
  return <div className="h-full w-full bg-background">{children}</div>;
};

export default TabLayout;
